import { apiBaseURL, release } from "./config"
import * as sentry from "./sentry"
import { initialLocale, langField } from "./i18n"
import { _ } from "svelte-i18n"
import { apiRequest } from "../../api/hooks/lib/ssr"
import { get } from "svelte/store"

// fetch polyfill
// [MIT License](LICENSE.md) © [Jason Miller](https://jasonformat.com/)
const _f = function (url: string, options?: { [key: string]: any }) {
    if (typeof XMLHttpRequest === "undefined") {
        return Promise.resolve(null)
    }

    options = options || {}
    return new Promise((resolve, reject) => {
        const request = new XMLHttpRequest()
        const keys: string[] = []
        const all = []
        const headers = {}

        const response = () => ({
            ok: ((request.status / 100) | 0) == 2, // 200-299
            statusText: request.statusText,
            status: request.status,
            url: request.responseURL,
            text: () => Promise.resolve(request.responseText),
            json: () => Promise.resolve(request.responseText).then(JSON.parse),
            blob: () => Promise.resolve(new Blob([request.response])),
            clone: response,
            headers: {
                // @ts-ignore
                keys: () => keys,
                // @ts-ignore
                entries: () => all,
                get: (n) => headers[n.toLowerCase()],
                has: (n) => n.toLowerCase() in headers,
            },
        })

        request.open(options.method || "get", url, true)

        request.onload = () => {
            request
                .getAllResponseHeaders()
                // @ts-ignore
                .replace(/^(.*?):[^\S\n]*([\s\S]*?)$/gm, (m, key, value) => {
                    keys.push((key = key.toLowerCase()))
                    all.push([key, value])
                    headers[key] = headers[key] ? `${headers[key]},${value}` : value
                })
            resolve(response())
        }

        request.onerror = reject

        request.withCredentials = options.credentials == "include"

        for (const i in options.headers) {
            request.setRequestHeader(i, options.headers[i])
        }

        request.send(options.body || null)
    })
}

// fetch must be declared after sentry import to get the hijacked fetch
export const _fetch = typeof fetch === "undefined" ? (typeof window === "undefined" ? _f : window.fetch || _f) : fetch

export const api = async <T>(endpoint: string, options?: ApiOptions): Promise<ApiResult<T>> => {
    const r = await apiRequest(endpoint, options, sentry, _fetch)
    return r
}

export const getContent = async (path: string): Promise<Content> => {
    const c = await api<Content[]>("content", { limit: 1, filter: { path } })
    if (c?.data?.length) {
        return c.data[0]
    }
    return null
}

export const getCategories = async () => api<Category[]>("category")

export const getProducts = async (filter?: any, sort?: string, limit?: number, offset?: number, projection?: string) =>
    api<Product[]>("product", {
        filter,
        sort,
        limit,
        offset,
        projection: projection || "teaser",
    })

export const getProduct = async (code: string): Promise<Product | null> => {
    const products = await getProducts({ code }, undefined, undefined, undefined, "full")
    if (products?.data?.length) {
        return products.data[0]
    }
    return null
}

let tree: CategoryNode
const pathMap: { [key: string]: CategoryNode } = {}

let _gettingTree = false

export const getCategoryTree = async (): Promise<CategoryNode> => {
    if (tree) return tree

    // check concurrent running
    while (_gettingTree) {
        // sleep 100ms and check again
        await new Promise((r) => setTimeout(r, 100))
    }
    if (tree) {
        return tree
    }

    _gettingTree = true
    const categories = await getCategories()

    let root: CategoryNode
    let map: { [key: string]: CategoryNode } = {}

    // find root and build map
    categories.data?.forEach((c) => {
        map[c.id] = { ...c, childObjects: [], allIds: [] }
        if (c.is_root && !root) root = map[c.id]
    })

    if (!root) {
        _gettingTree = false
        return {
            id: "unknown",
            allIds: [],
            childObjects: [],
            name: { de: "NO ROOT FOUND" },
            path: { de: "/" },
        }
    }

    // connect nodes
    categories.data?.forEach((c) => {
        if (c.children?.length) {
            c.children.forEach((child) => {
                map[c.id].childObjects.push(map[child])
            })
        }
        pathMap[langField(c.path, "")] = map[c.id]
    })

    try {
        const _getTreeIds = (n: CategoryNode): string[] => {
            n.allIds = [n.id]
            n.childObjects.forEach((c) => {
                c.parent = langField(n.path, "")
                n.allIds.push(..._getTreeIds(c))
            })

            return n.allIds
        }
        _getTreeIds(root)
    } catch (e) {
        console.error(e)
        throw e
    } finally {
        _gettingTree = false
    }

    tree = root // caching

    _gettingTree = false
    return root
}
export const getCategoryPathMap = async () => {
    if (!tree) await getCategoryTree()
    return pathMap
}

export const getCountries = async (filter?: any, sort?: string, limit?: number, offset?: number) => {
    const result = await api<Country[]>("country", {
        filter,
        sort,
        limit,
        offset,
    })
    // @ts-ignore
    result.data = result.data?.map((r) => {
        r.vat = parseFloat(r.vat?.toString())
        r.delivery_costs_net = parseFloat(r.delivery_costs_net?.toString() || "0")
        r.handling_costs_net = parseFloat(r.handling_costs_net?.toString() || "0")
        r.min_quantity_surcharge_net = parseFloat(r.min_quantity_surcharge_net?.toString() || "0")
        return r
    })

    return result
}

let countryList: Country[]
let countryMap: {
    [key: string]: Country
} = {}

export const getCountriesList = async () => {
    if (countryList) return countryList

    const result = await getCountries(undefined, "name." + initialLocale)

    countryList = result.data

    countryList?.forEach((c) => {
        countryMap[c.code] = c
    })

    return countryList
}

export const getCountry = async (code: string) => {
    if (!countryList) await getCountriesList()
    return countryMap[code]
}

export const postOrder = async (order: Order) => {
    console.log("release", release)
    console.log("postOrder", order)
    const response = await _fetch(apiBaseURL + "order?locale=" + get(_)("locale"), {
        method: "POST",
        mode: "cors",
        headers: {
            "Content-Type": "application/json",
            token: "owshwerNwoa",
        },
        body: JSON.stringify(order),
    })
    // @ts-ignore
    let data = await response.json()

    if (response.status < 200 || response.status >= 400) throw { response, data }

    return data
}

export const postProductInquiry = async (inquiry: ProductInquiry, checksum: string) => {
    console.log("release", release)
    if (!inquiry.dealerChoice || !inquiry.dealerChoice.number) {
        inquiry.dealerChoice = null
    }
    console.log("postProductInquiry", inquiry)
    const response = await _fetch(apiBaseURL + "product-inquiry?locale=" + get(_)("locale") + "&cs=" + checksum, {
        method: "POST",
        mode: "cors",
        headers: {
            "Content-Type": "application/json",
            token: "owshwerNwoa",
        },
        body: JSON.stringify(inquiry),
    })
    // @ts-ignore
    let data = await response.json()

    if (response.status < 200 || response.status >= 400) throw { response, data }

    return data
}

export const postOfferAssistantInquiry = async (inquiry: ProductInquiry, checksum: string) => {
    // Not needed properties for offer assistant inquiry
    delete inquiry.cart
    delete inquiry.vat
    delete inquiry.sum
    if (!inquiry.dealerChoice || !inquiry.dealerChoice.number) {
        inquiry.dealerChoice = null
    }
    console.info("API :: POST - offer-assistant-inquiry", inquiry)

    const response = await _fetch(
        apiBaseURL + "offer-assistant-inquiry?locale=" + get(_)("locale") + "&cs=" + checksum,
        {
            method: "POST",
            mode: "cors",
            headers: {
                "Content-Type": "application/json",
                token: "owshwerNwoa",
            },
            body: JSON.stringify(inquiry),
        }
    )
    // @ts-ignore
    let data = await response.json()

    if (response.status < 200 || response.status >= 400) throw { response, data }

    return data
}

export const postAccessToken = async (at: AccessToken) => {
    const response = await _fetch(apiBaseURL + "access_token?locale=" + get(_)("locale"), {
        method: "POST",
        mode: "cors",
        headers: {
            "Content-Type": "application/json",
            token: "owshwerNwoa",
        },
        body: JSON.stringify(at),
    })
    // @ts-ignore
    let data = await response.json()

    if (response.status < 200 || response.status >= 400) throw { response, data }

    return data
}

export const getMyOrders = async (
    accessToken: string,
    filter?: any,
    sort?: string,
    limit?: number,
    offset?: number
) => {
    const result = await api<Order[]>("order", {
        filter,
        sort,
        limit,
        offset,
        headers: {
            token: "owshwerNwoa",
        },
        params: {
            t: accessToken,
        },
    })
    return result
}

export const getMyOrder = async (accessToken: string, id: string): Promise<Order> => {
    const r = await getMyOrders(accessToken, { _id: id })
    if (r.data?.length) return r.data[0]
    return null
}

export const getRatableProducts = async (orderNumber: string, postalcode: string) => {
    const result = await api<Order[]>(`order`, {
        params: {
            postalcode,
            orderNumber,
        },
        headers: {
            "Content-Type": "application/json",
            token: "owshwerNwoa",
        },
        projection: "rating",
    })
    return result.data[0]
}

export const getRating = async (orderNumber: string, postalcode: string) => {
    const result = await api<OrderRating[]>("rating", {
        params: {
            orderNumber,
            postalcode,
            rateIt: "true",
        },
    })
    return result
}

//need rating obj by id or sth...

export const submitRating = async (rating: OrderRating) => {
    const response = await _fetch(`${apiBaseURL}rating${rating.usePutForRating ? `/${rating.id}` : ""}`, {
        method: rating.usePutForRating ? "PUT" : "POST",
        mode: "cors",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(rating),
    })

    // @ts-ignore
    let data = await response.json()
    if (response.status < 200 || response.status >= 400) throw { response, data }
    return data
}

export const postContactForm = async (formData: any, checksum: string) => {
    const response = await _fetch(apiBaseURL + "contact_form?locale=" + get(_)("locale") + "&cs=" + checksum, {
        method: "POST",
        mode: "cors",
        headers: {
            "Content-Type": "application/json",
            token: "owshwerNwoa",
        },
        body: JSON.stringify(formData),
    })
    // @ts-ignore
    let data = await response.json()

    if (response.status < 200 || response.status >= 400) throw { response, data }

    return data
}

export const postPayment = async (payment: Payment): Promise<Payment> => {
    console.log("release", release)
    console.log("postPayment", payment)
    const response = await _fetch(apiBaseURL + "payment?locale=" + get(_)("locale"), {
        method: "POST",
        mode: "cors",
        headers: {
            "Content-Type": "application/json",
            token: "owshwerNwoa",
        },
        body: JSON.stringify(payment),
    })
    let data = await response.json()

    if (response.status < 200 || response.status >= 400) throw { response, data }

    return data
}

export const getDealer = async (options: {
    online?: boolean
    countrycode?: string
    postcode?: string
    range?: number
    dealerNumber?: number | string | Array<number> | Array<string>
}) => {
    const params: any = {}
    let doQuery = false
    if (options?.online === true && options?.countrycode) {
        params.online_shop = true
        params.countrycode = options.countrycode
        doQuery = true
    } else if (options?.postcode && options?.range && options?.countrycode) {
        params.countrycode = options.countrycode
        params.postcode = options.postcode
        params.range = options.range
        doQuery = true
    } else if (options?.countrycode) {
        params.countrycode = options.countrycode
        doQuery = true
    }

    if (options?.dealerNumber) {
        params.dealerNumber = Array.isArray(options.dealerNumber)
            ? options.dealerNumber.join(",")
            : options.dealerNumber

        doQuery = true
    }

    if (!doQuery) {
        return {
            count: 0,
            data: [],
        }
    }
    const result = await api<Dealer[]>("dealer", {
        params,
        sort: "company",
        headers: {
            token: "owshwerNwoa",
        },
    })
    return result
}

export const getOfferAssistantConfig = async (name: string) => {
    const response = await api<OfferAssistantConfig[]>(`offer-assistant-config`, {
        filter: {
            name,
        },
    })
    return response
}
